import {
    buildAppDataFromRetentionState,
    buildMwSettings,
    buildRequestBody,
    executeRetentionPipelines,
    getInitialState,
    getRetentionInsights,
    transformInsightsResponse,
    wrapMwResultsAsArtifact,
} from './retention-trends.utils.js';
import { getAppId } from '@/vuex/modules/trends/trends-settings.module.js';
import { StreamingProcessor } from '../ask-pendo/ask-pendo.streaming-processor.js';

const MIDDLEWARE_FLAG = 'newSignalTypes';

const retentionTrends = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setRetentionInsights (state, { insights }) {
            state.insights = insights;
        },
        setInsightRequestBody (state, { requestBody }) {
            state.insightRequestBody = requestBody;
        },
        setLoading (state, { isLoading }) {
            state.isLoading = isLoading;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async fetchRetentionInsights ({ commit, rootGetters, dispatch }, { options = {} } = {}) {
            commit('setLoading', { isLoading: true });
            commit('setError', { error: null });

            try {
                const appId = await getAppId(rootGetters, dispatch);
                const includeAnonymous = false;

                const requestBody = buildRequestBody(appId, includeAnonymous, rootGetters);
                commit('setInsightRequestBody', { requestBody });

                const useMiddleware = !!rootGetters['auth/hasSegmentFlag']?.(MIDDLEWARE_FLAG);

                if (useMiddleware) {
                    const settings = buildMwSettings(appId, includeAnonymous, rootGetters);
                    const executed = await executeRetentionPipelines(settings);
                    const synthetic = wrapMwResultsAsArtifact(appId, executed);

                    const appData = buildAppDataFromRetentionState(synthetic, rootGetters, appId);
                    const transformed = transformInsightsResponse(synthetic, appData);

                    commit('setRetentionInsights', { insights: transformed });

                    return transformed;
                }

                const data = await getRetentionInsights(requestBody, options);

                if (data instanceof ReadableStream) {
                    const streamingProcessor = new StreamingProcessor({
                        onData: (_chunk) => {},
                        onEnd: (finalState) => {
                            if (!finalState) {
                                commit('setError', { error: 'Stream ended without final state' });

                                return;
                            }

                            const appData = buildAppDataFromRetentionState(finalState, rootGetters, appId);
                            const transformedInsights = transformInsightsResponse(finalState, appData);

                            commit('setRetentionInsights', { insights: transformedInsights });
                        },
                        onError: (error) => {
                            commit('setError', { error: error.message || 'Failed to fetch retention insights' });
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                const appData = buildAppDataFromRetentionState(data?.final_state, rootGetters, appId);
                const transformedInsights = transformInsightsResponse(data.final_state, appData);

                commit('setRetentionInsights', { insights: transformedInsights });

                return transformedInsights;
            } catch (error) {
                commit('setError', { error: error.message || 'Failed to fetch retention insights' });
                throw error;
            } finally {
                commit('setLoading', { isLoading: false });
            }
        },
    },
    getters: {
        retentionInsights: (state) => state.insights || [],
        insightRequestBody: (state) => state.insightRequestBody,
        isLoading: (state) => state.isLoading,
        hasError: (state) => !!state.error,
        error: (state) => state.error,
    },
};

export default retentionTrends;
